import { OPEN_CLINICAL_TASK_STATUSES } from '@bariendo/lib/clinical-tasks';
import { TaskGroupIdentifier, TaskStatus } from '@bariendo/lib/constants';
import { useDebouncedCallback } from '@mantine/hooks';
import { getReferenceString } from '@medplum/core';
import { Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSubscriptionWithLimitHandling } from '../services/subscriptions';
import { useCurrentUser } from './useCurrentUser';

/**
 * The left-nav Inbox badge: unread notifications PLUS open clinical tasks
 * (individually assigned to this user, and assigned to their role) — the
 * single count for everything the inbox surfaces.
 *
 * Clinical tasks count as OPEN rather than unread: read/unread is
 * deliberately a no-op for them (see changeNotificationStatus), so "unread"
 * carries no meaning — an assigned task needs attention until it's done.
 * No double counting: individually assigned tasks carry no performerType,
 * role-assigned tasks no Practitioner owner.
 */
export const useNotificationsCount = () => {
  const profile = useMedplumProfile() as Practitioner;
  const medplum = useMedplum();
  const { currentUserRole } = useCurrentUser();
  const [unreadCount, setUnreadCount] = useState(0);

  const query = useMemo(() => {
    if (!medplum.isAuthenticated() || !profile?.id) return;

    const queryParams = new URLSearchParams({
      'group-identifier': `${TaskGroupIdentifier.RESOURCE_ASSIGNMENT},${TaskGroupIdentifier.PROVIDER_MENTION},${TaskGroupIdentifier.GENERAL_NOTIFICATION}`,
      owner: getReferenceString(profile)!,
    });

    return `Task?${queryParams.toString()}`;
  }, [profile, medplum]);

  const ownedTasksQuery = useMemo(() => {
    if (!medplum.isAuthenticated() || !profile?.id) return;

    const queryParams = new URLSearchParams({
      'group-identifier': TaskGroupIdentifier.ORBIT_PROVIDER_TASK,
      owner: getReferenceString(profile)!,
      status: OPEN_CLINICAL_TASK_STATUSES.join(','),
    });

    return `Task?${queryParams.toString()}`;
  }, [profile, medplum]);

  const roleTasksQuery = useMemo(() => {
    if (!medplum.isAuthenticated() || !currentUserRole) return;

    const queryParams = new URLSearchParams({
      'group-identifier': TaskGroupIdentifier.ORBIT_PROVIDER_TASK,
      performer: currentUserRole,
      status: OPEN_CLINICAL_TASK_STATUSES.join(','),
    });

    return `Task?${queryParams.toString()}`;
  }, [currentUserRole, medplum]);

  // Owner axis: the three notification families + person-assigned clinical
  // tasks, all keyed on owner = this practitioner.
  const ownerSubscriptionCriteria = useMemo(() => {
    if (!medplum.isAuthenticated() || !profile?.id) return;

    const queryParams = new URLSearchParams({
      'group-identifier': `${TaskGroupIdentifier.RESOURCE_ASSIGNMENT},${TaskGroupIdentifier.PROVIDER_MENTION},${TaskGroupIdentifier.GENERAL_NOTIFICATION},${TaskGroupIdentifier.ORBIT_PROVIDER_TASK}`,
      owner: getReferenceString(profile)!,
    });

    return `Task?${queryParams.toString()}`;
  }, [profile, medplum]);

  const roleSubscriptionCriteria = useMemo(() => {
    if (!medplum.isAuthenticated() || !currentUserRole) return;

    const queryParams = new URLSearchParams({
      'group-identifier': TaskGroupIdentifier.ORBIT_PROVIDER_TASK,
      performer: currentUserRole,
    });

    return `Task?${queryParams.toString()}`;
  }, [currentUserRole, medplum]);

  const fetchUnreadCount = useCallback(async () => {
    if (!medplum.isAuthenticated() || !query) return;

    const countOf = async (path: string | undefined): Promise<number> => {
      if (!path) {
        return 0;
      }
      // cache: 'reload' — subscription-driven, and the 60s GET cache is only
      // invalidated by local writes.
      const bundle = await medplum.get(medplum.fhirUrl(`${path}&_summary=count`), { cache: 'reload' });
      return bundle.total ?? 0;
    };

    const [notifications, ownedTasks, roleTasks] = await Promise.all([
      countOf(`${query}&status=${TaskStatus.READY}`),
      countOf(ownedTasksQuery),
      countOf(roleTasksQuery),
    ]);
    setUnreadCount(notifications + ownedTasks + roleTasks);
  }, [medplum, query, ownedTasksQuery, roleTasksQuery]);

  const debouncedFetchUnreadCount = useDebouncedCallback(fetchUnreadCount, 1000);

  // The debounced callback has a STABLE identity, so this effect must depend on
  // the query strings too: currentUserRole resolves asynchronously, and without
  // them a role arriving after the first tick would leave role tasks
  // permanently uncounted until some unrelated Task event fired.
  useEffect(() => {
    debouncedFetchUnreadCount();
  }, [debouncedFetchUnreadCount, query, ownedTasksQuery, roleTasksQuery]);

  // Subscriptions are a scarce per-user resource (see subscriptions.ts limit
  // handling), so scope them tightly and keep the count at two:
  //   1. everything owned by this user — notifications AND person-assigned
  //      clinical tasks, which share the owner axis;
  //   2. clinical tasks for this user's role.
  // Status is deliberately omitted from both: a task LEAVING the open set
  // (completed/cancelled) must still fire so the badge drops it.
  useSubscriptionWithLimitHandling(ownerSubscriptionCriteria ?? '', debouncedFetchUnreadCount);
  useSubscriptionWithLimitHandling(roleSubscriptionCriteria ?? '', debouncedFetchUnreadCount);

  return unreadCount;
};
